import React, { useEffect, useState } from "react";
import { ContentSectionHeader, PageHeader } from "../../../components/common";
import { RefreshCw } from "lucide-react";
import { Button } from "../../../components";
import { useToast } from "../../../components";
import { useTranslation } from "react-i18next";
import { ConfirmationDialog } from "../../../components/confirmation-dialog";
import { useNavigate } from "react-router-dom";
import { Input } from "../../../components/input";
import type { MerchantFilters, MerchantListItem } from "../types/merchant.types";
import { MerchantListView } from "../components/merchant-table";
import MerchantStatsSection from "../components/MerchantStatsSection";
import { getMerchantDisplayName, getMerchantStatus } from "../utils/merchant.utils";
import { suspendMerchantSchema } from "../components/merchant-form/merchant.scheme";
import { getApiErrorMessage } from "../../../lib/result-codes";
import {
  useApproveMerchant,
  useDeleteMerchant,
  useGetMerchantList,
  useMerchantOptions,
  useMerchantStatistics,
  useSuspendMerchant,
} from "../hooks";

type MerchantListPageProps = {
  basePath?: string;
};

function MerchantListPage({ basePath = "/merchant-management" }: MerchantListPageProps) {
  const { t } = useTranslation();
  const {
    merchants,
    isLoading,
    isError,
    error,
    refreshMerchants,
    request,
    haveFilter,
    setFilter,
    setPagination,
    setSorting,
  } = useGetMerchantList();
  const { deleteMerchant, loading: isDeleting } = useDeleteMerchant();
  const { approveMerchant, loading: isApproving } = useApproveMerchant();
  const { suspendMerchant, loading: isSuspending } = useSuspendMerchant();
  const { categoryOptions, verificationOptions, badgeOptions, fetchCategoryOptions } =
    useMerchantOptions();

  const {
    data: merchantStats,
    loading: statsLoading,
    refresh: refreshMerchantStats,
  } = useMerchantStatistics();
  const { addToast } = useToast();
  const [merchantToDelete, setMerchantToDelete] = useState<MerchantListItem | null>(null);
  const [merchantToApprove, setMerchantToApprove] = useState<MerchantListItem | null>(null);
  const [merchantToSuspend, setMerchantToSuspend] = useState<MerchantListItem | null>(null);
  const [suspendReason, setSuspendReason] = useState("");
  const [suspendReasonError, setSuspendReasonError] = useState<string | null>(null);
  const itemsPerPage = 20;
  const totalPages = Math.ceil(request.pagination.totalCount / itemsPerPage);

  const navigate = useNavigate();

  const handleDateSort = (nextDirection: "asc" | "desc" | null) => {
    setSorting(
      nextDirection ? { sort_key: "created_at", sort_direction: nextDirection } : undefined,
    );
  };

  const handleConfirmDelete = async () => {
    if (!merchantToDelete) return;

    try {
      await deleteMerchant(merchantToDelete.id);
      await refreshMerchants();
      refreshMerchantStats();
      addToast({
        type: "success",
        title: "Success!",
        message: t("merchant.delete_success"),
      });
      setMerchantToDelete(null);
    } catch (deleteError: unknown) {
      addToast({
        type: "error",
        title: "Error!",
        message: getApiErrorMessage(deleteError, t("merchant.delete_error")),
      });
    }
  };

  const handleConfirmApprove = async () => {
    if (!merchantToApprove) return;

    try {
      await approveMerchant(merchantToApprove.id);
      await refreshMerchants();
      refreshMerchantStats();
      addToast({
        type: "success",
        title: "Success!",
        message: t("merchant.approve_success"),
      });
      setMerchantToApprove(null);
    } catch (approveError: unknown) {
      addToast({
        type: "error",
        title: "Error!",
        message: getApiErrorMessage(approveError, t("merchant.approve_error")),
      });
    }
  };

  const handleConfirmSuspend = async () => {
    if (!merchantToSuspend) return;

    const parsed = suspendMerchantSchema.safeParse({ reason: suspendReason });
    if (!parsed.success) {
      setSuspendReasonError(parsed.error.issues[0]?.message ?? t("merchant.reason_required"));
      return;
    }
    setSuspendReasonError(null);

    try {
      await suspendMerchant(merchantToSuspend.id, parsed.data.reason);
      await refreshMerchants();
      refreshMerchantStats();
      addToast({
        type: "success",
        title: "Success!",
        message: t("merchant.suspend_success"),
      });
      setMerchantToSuspend(null);
      setSuspendReason("");
    } catch (suspendError: unknown) {
      addToast({
        type: "error",
        title: "Error!",
        message: getApiErrorMessage(suspendError, t("merchant.suspend_error")),
      });
    }
  };

  const handleRefreshAll = () => {
    refreshMerchants();
    refreshMerchantStats();
  };

  const stats = [
    {
      label: t("merchant.total_merchants"),
      value: merchantStats?.total_merchants ?? request.pagination.totalCount,
    },
    {
      label: t("merchant.pending_merchants"),
      value:
        merchantStats?.pending_merchants ??
        merchants.filter((m) => getMerchantStatus(m) === "pending").length,
    },
    {
      label: t("merchant.verified_merchants"),
      value:
        merchantStats?.verified_merchants ??
        merchants.filter((m) => getMerchantStatus(m) === "verified").length,
    },
    {
      label: t("merchant.suspended_merchants"),
      value:
        merchantStats?.suspended_merchants ??
        merchants.filter((m) => getMerchantStatus(m) === "suspended").length,
    },
  ];

  useEffect(() => {
    fetchCategoryOptions();
  }, [fetchCategoryOptions]);

  return (
    <div className="min-h-screen bg-background">
      <PageHeader
        title={t("merchant.management")}
        subtitle={t("merchant.subtitle")}
        sticky
      />

      <div className="px-6 py-6">
        <MerchantStatsSection
          stats={stats}
          loading={statsLoading}
        />

        <ContentSectionHeader
          title={t("merchant.merchant_list")}
          actions={
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefreshAll}
              disabled={isLoading || statsLoading}
              className="flex items-center gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${isLoading || statsLoading ? "animate-spin" : ""}`} />
              {haveFilter ? t("common.clear") : t("ui.refresh")}
            </Button>
          }
        />

        <MerchantListView
          loading={isLoading}
          error={isError ? error : null}
          onRetry={refreshMerchants}
          merchants={merchants}
          filters={request.filters}
          updateFilter={setFilter as React.Dispatch<React.SetStateAction<MerchantFilters>>}
          dateSortDirection={request.sorting?.sort_direction ?? null}
          onDateSort={handleDateSort}
          categoryOptions={categoryOptions}
          verificationOptions={verificationOptions}
          badgeOptions={badgeOptions}
          onRowOpen={(merchant) => navigate(`${basePath}/view/${merchant.id}`)}
          onApprove={setMerchantToApprove}
          onSuspend={(merchant) => {
            setMerchantToSuspend(merchant);
            setSuspendReason("");
            setSuspendReasonError(null);
          }}
          onDelete={setMerchantToDelete}
          page={request.pagination.page}
          totalPages={totalPages}
          onPageChange={(value) => setPagination((prev) => ({ ...prev, page: value }))}
        />
      </div>

      <ConfirmationDialog
        isOpen={Boolean(merchantToDelete)}
        onClose={() => setMerchantToDelete(null)}
        onConfirm={handleConfirmDelete}
        title={t("merchant.delete_merchant")}
        description={t("merchant.delete_confirm", {
          name: getMerchantDisplayName(merchantToDelete ?? { business_name: "", email: "" }),
        })}
        confirmText={t("common.delete")}
        cancelText={t("common.cancel")}
        isLoading={isDeleting}
        type="danger"
      />

      <ConfirmationDialog
        isOpen={Boolean(merchantToApprove)}
        onClose={() => setMerchantToApprove(null)}
        onConfirm={handleConfirmApprove}
        title={t("merchant.approve_merchant")}
        description={t("merchant.approve_confirm", {
          name: getMerchantDisplayName(merchantToApprove ?? { business_name: "", email: "" }),
        })}
        confirmText={t("common.approve")}
        cancelText={t("common.cancel")}
        isLoading={isApproving}
        type="info"
      />

      <ConfirmationDialog
        isOpen={Boolean(merchantToSuspend)}
        onClose={() => {
          setMerchantToSuspend(null);
          setSuspendReason("");
          setSuspendReasonError(null);
        }}
        onConfirm={handleConfirmSuspend}
        title={t("merchant.suspend_merchant")}
        description={
          <div className="flex flex-col gap-2 text-left">
            <span>
              {t("merchant.suspend_confirm", {
                name: getMerchantDisplayName(merchantToSuspend ?? { business_name: "", email: "" }),
              })}
            </span>
            <Input
              name="reason"
              value={suspendReason}
              onChange={(event) => setSuspendReason(event.target.value)}
              placeholder={t("merchant.reason_placeholder")}
              className="rounded-xl"
            />
            {suspendReasonError ? (
              <span className="text-sm text-red-600 dark:text-red-400">{suspendReasonError}</span>
            ) : null}
          </div>
        }
        confirmText={t("merchant.suspend")}
        cancelText={t("common.cancel")}
        isLoading={isSuspending}
        type="warning"
      />
    </div>
  );
}

export default MerchantListPage;
